import { writable, derived, type Unsubscriber } from 'svelte/store';
import type { Filters, CompanyWithContacts } from '$lib/types/api';

export const queryResult = writable<CompanyWithContacts[]>([]);
export const resultStats = writable<Record<string, unknown>>({});
export const queryRunning = writable<boolean>(false);

/**
 * Whether a search has been run for the selection now on screen.
 *
 * It decides which empty screen is right: the guide, for somebody who has not
 * searched yet, or "nothing found", for somebody who has. It was a variable
 * inside Results.svelte that latched true and never cleared, so once anything
 * had been searched the guide could not be shown again — starting over left the
 * customer looking at "No Results Found" with nothing on screen to fix it.
 */
export const hasSearched = writable<boolean>(false);
export const scoreFilterRange = writable<[number, number]>([0, 100]);

/**
 * The companies this selection may draw from, when the filters cannot say.
 *
 * # What it is for
 *
 * Most selections are reproducible: the filters and the chosen segments
 * describe them, so re-filtering means asking the search again and the answer
 * is complete. Some are not. A saved list is a set somebody assembled by hand;
 * a full-scope search answered a phrase that lives in no filter. Neither can be
 * described to `/deep-iq/ids` by the filters alone, so narrowing one means
 * naming its members — which is what the endpoint's `company_ids` is for.
 *
 * Null means reproducible, and re-filtering runs an ordinary search.
 *
 * # It holds what was seated, not what is on screen
 *
 * Deliberately. Narrowing against the *current* rows makes filters one-way:
 * the companies a canton filter removed are gone, so clearing it cannot bring
 * them back, and the customer is left with a list that only ever shrinks.
 * Against the seated set, every submit re-narrows the whole list and clearing a
 * filter restores what it had excluded.
 */
export const selectionScope = writable<string[] | null>(null);

/**
 * A selection with nothing chosen.
 *
 * A function rather than a constant, because every caller gets its own copy:
 * one shared object would be mutated by whoever reset last and the "empty"
 * filters would slowly fill up with somebody else's search.
 *
 * It exists because three places used to write this shape out by hand — the
 * store below, the mode switcher, and the confirmation dialog — and the copies
 * had already drifted: two of them predate `contact_titles` and
 * `exclude_list_code`, so switching mode left a job-title filter and a
 * subtracted customer list behind on a selection that showed neither.
 */
export function defaultFilters(): Filters {
  return {
    postal_radius: { center_postal_code: '', radius_km: 0 },
    postal_codes: [],
    cantons: [],
    languages: [],
    company_size_range: [null, null],
    founding_year_range: [null, null],
    contact_roles: [],
    contact_titles: [],
    contact_mode: 'single',
    structured_filters: {
      branch_office_count: { min: 0 },
      has_website: false,
      has_email: false,
      has_phone: false,
      has_facebook: false,
      has_linkedin: false,
      has_instagram: false,
      has_twitter: false,
      legal_form: [],
      exclude_legal_form: [],
      capital: null,
      has_revisionsverzicht: false,
      revision_company: '',
      offers_apprenticeships: false,
      is_hiring: false,
      technologies: [],
      mail_gaps: [],
      certifications: [],
      group_roles: [],
      sales_models: []
    },
    signal_filters: undefined,
    embedding_query: '',
    text_constraints: [],
    exclude_list_code: ''
  };
}

export const queryFilters = writable<Filters>(defaultFilters());

/**
 * A writable view onto one field of `queryFilters`.
 *
 * Generic in the field's type, so the getter and setter passed at each call
 * site infer from `Filters` rather than each being an implicit `any`. That is
 * most of this file: there are thirty of these bindings and every one of them
 * declared two untyped callbacks.
 *
 * The returned object is store-shaped — `subscribe` and `set` — which is all
 * `bind:value` and the `$store` prefix need.
 */
interface NestedBinding<T> {
  subscribe: (run: (value: T) => void) => Unsubscriber;
  set: (value: T) => void;
  update: (updater: (value: T) => T) => void;
}

function nestedBinding<T>(
  getter: (filters: Filters) => T,
  setter: (filters: Filters, value: T) => Filters
): NestedBinding<T> {
  return {
    subscribe: (run) => queryFilters.subscribe((f) => run(getter(f))),
    set: (value) => queryFilters.update((f) => setter(f, value)),
    // Some callers amend a list rather than replace it — the postal-code and
    // canton filters both do.
    update: (updater) => queryFilters.update((f) => setter(f, updater(getter(f))))
  };
}

// Postal radius
export const centerPostalCode = nestedBinding(
  (f) => f.postal_radius.center_postal_code,
  (f, v) => ({
    ...f,
    postal_radius: {
      ...f.postal_radius,
      center_postal_code: v
    }
  })
);

export const radiusKm = nestedBinding(
  (f) => f.postal_radius.radius_km,
  (f, v) => ({
    ...f,
    postal_radius: {
      ...f.postal_radius,
      radius_km: v
    }
  })
);

// Arrays and top-level
export const postalCodes = nestedBinding(
  (f) => f.postal_codes,
  (f, v) => ({ ...f, postal_codes: v })
);
export const cantons = nestedBinding(
  (f) => f.cantons,
  (f, v) => ({ ...f, cantons: v })
);
export const languages = nestedBinding(
  (f) => f.languages,
  (f, v) => ({ ...f, languages: v })
);
export const companySizeRange = nestedBinding(
  (f) => f.company_size_range,
  (f, v) => ({ ...f, company_size_range: v })
);
export const foundingYearRange = nestedBinding(
  (f) => f.founding_year_range,
  (f, v) => ({ ...f, founding_year_range: v })
);
export const contactRoles = nestedBinding(
  (f) => f.contact_roles,
  (f, v) => ({ ...f, contact_roles: v })
);

export const contactTitles = nestedBinding(
  (f) => f.contact_titles ?? [],
  (f, v) => ({ ...f, contact_titles: v })
);

export const contactMode = nestedBinding(
  (f) => f.contact_mode,
  (f, v) => ({ ...f, contact_mode: v })
);

// Structured filters
export const branchOfficeCountMin = nestedBinding(
  (f) => f.structured_filters.branch_office_count.min,
  (f, v) => ({
    ...f,
    structured_filters: {
      ...f.structured_filters,
      branch_office_count: { ...f.structured_filters.branch_office_count, min: v }
    }
  })
);

export const hasWebsite = nestedBinding(
  (f) => f.structured_filters.has_website,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, has_website: v }
  })
);

export const hasEmail = nestedBinding(
  (f) => f.structured_filters.has_email,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, has_email: v }
  })
);

export const hasPhone = nestedBinding(
  (f) => f.structured_filters.has_phone,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, has_phone: v }
  })
);

export const hasFacebook = nestedBinding(
  (f) => f.structured_filters.has_facebook,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, has_facebook: v }
  })
);

export const hasLinkedin = nestedBinding(
  (f) => f.structured_filters.has_linkedin,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, has_linkedin: v }
  })
);

export const hasInstagram = nestedBinding(
  (f) => f.structured_filters.has_instagram,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, has_instagram: v }
  })
);

export const hasTwitter = nestedBinding(
  (f) => f.structured_filters.has_twitter,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, has_twitter: v }
  })
);

/**
 * Companies that train apprentices.
 *
 * Grouped with the flags above because it binds the same way, and separated
 * from them in the panel because it asks a different kind of question: the
 * others are about whether we can reach a company, this is about the company.
 */
export const offersApprenticeships = nestedBinding(
  (f) => f.structured_filters.offers_apprenticeships,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, offers_apprenticeships: v }
  })
);

/**
 * Companies advertising a position.
 *
 * The second flag that asks about the company rather than about reaching it, so
 * it sits beside apprenticeships for the same reason and not in the channel
 * grid.
 *
 * What it does not mean is the part worth knowing: only companies that
 * advertise on jobs.ch can ever be true, recruitment agencies are deliberately
 * excluded because their vacancies belong to clients they do not name, and a
 * quarter of the advertisements carry no company identifier at all. So an
 * unticked box is the normal state and a false is never evidence that a company
 * is not hiring.
 */
export const isHiring = nestedBinding(
  (f) => f.structured_filters.is_hiring,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, is_hiring: v }
  })
);

/**
 * The technologies a company must be using, and the mail gaps it must have.
 *
 * Lists rather than flags, because there are 244 of the first and they are
 * chosen from a server-held vocabulary. `update` matters more here than for the
 * other bindings: the panel adds and removes one slug at a time, and replacing
 * the whole array on every tick would make the component own the list.
 */
export const technologies = nestedBinding(
  (f) => f.structured_filters.technologies ?? [],
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, technologies: v }
  })
);

export const mailGaps = nestedBinding(
  (f) => f.structured_filters.mail_gaps ?? [],
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, mail_gaps: v }
  })
);

/** The transport licence filter, or null for none. */
export const fleetFilter = nestedBinding(
  (f) => f.structured_filters.fleet ?? null,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, fleet: v }
  })
);

/** The company's place in a group: "parent", "subsidiary", or both for either. */
export const groupRoles = nestedBinding(
  (f) => f.structured_filters.group_roles ?? [],
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, group_roles: v }
  })
);

/** Whom the company mainly sells to: "b2b", "b2b_and_b2c", "b2c", "b2g"; any of them. */
export const salesModels = nestedBinding(
  (f) => f.structured_filters.sales_models ?? [],
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, sales_models: v }
  })
);

/** The certifications a company must state, as slugs. */
export const certifications = nestedBinding(
  (f) => f.structured_filters.certifications ?? [],
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, certifications: v }
  })
);

export const legalForm = nestedBinding(
  (f) => f.structured_filters.legal_form,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, legal_form: v }
  })
);

/**
 * The forms a search removes.
 *
 * Held as the register's numbers, which is what the filter panel has always
 * sent. The assistant may set it with words instead; the API resolves both.
 */
export const excludeLegalForm = nestedBinding(
  (f) => f.structured_filters.exclude_legal_form,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, exclude_legal_form: v }
  })
);

export const capital = nestedBinding(
  (f) => f.structured_filters.capital,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, capital: v }
  })
);

export const hasRevisionsverzicht = nestedBinding(
  (f) => f.structured_filters.has_revisionsverzicht,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, has_revisionsverzicht: v }
  })
);

export const revisionCompany = nestedBinding(
  (f) => f.structured_filters.revision_company,
  (f, v) => ({
    ...f,
    structured_filters: { ...f.structured_filters, revision_company: v }
  })
);

// Signal filters
export const signalFilters = nestedBinding(
  (f) => f.signal_filters,
  (f, v) => ({ ...f, signal_filters: v })
);

// Free-text
export const embeddingQuery = nestedBinding(
  (f) => f.embedding_query,
  (f, v) => ({ ...f, embedding_query: v })
);
export const textConstraints = nestedBinding(
  (f) => f.text_constraints,
  (f, v) => ({ ...f, text_constraints: v })
);

// Derived store for filtered results based on score range
export const filteredQueryResult = derived(
  [queryResult, scoreFilterRange],
  ([$queryResult, $scoreFilterRange]) => {
    const [minScore, maxScore] = $scoreFilterRange;

    // If range is default (0-100), return all results
    if (minScore === 0 && maxScore === 100) {
      return $queryResult;
    }

    // Filter results based on quality score
    // Convert percentage range (0-100) to decimal (0-1) for comparison
    const minScoreDecimal = minScore / 100;
    const maxScoreDecimal = maxScore / 100;

    return $queryResult.filter((company) => {
      const score = company.quality || 0;
      return score >= minScoreDecimal && score <= maxScoreDecimal;
    });
  }
);
